import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePermission } from "../../../hooks/usePermission";
import { permissionError, warning } from "../../../util/Alert";
import { baseURL } from "../../../util/config";
import Button from "../../extra/Button";
import Iconb from "../../extra/Iconb";
import ImagePreviewModal from "../../extra/ImagePreviewModal";
import Pagination from "../../extra/Pagination";
import Searching from "../../extra/Searching";
import Table from "../../extra/Table";
import ToggleSwitch from "../../extra/ToggleSwitch";
import { OPEN_DIALOGUE } from "../../store/dialogue/dialogue.type";
import { deleteLanguage, downloadTranslationsCSV, getAllLanguages, toggleLanguage } from "../../store/language/language.action";


const LanguageTable = () => {
    const dispatch = useDispatch();
    const { can } = usePermission();

    const canCreate = can("App Language", "Create");
    const canEdit = can("App Language", "Edit");
    const canDelete = can("App Language", "Delete");

    const { languages, totalLanguages } = useSelector((state) => state.language);
    const hasPermission = useSelector((state) => state.admin.admin.flag);

    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [search, setSearch] = useState("");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            setLoading(false);
        }, 1500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        dispatch(getAllLanguages({ start: page, limit: size, search }));
    }, [dispatch, page, size, search]);

    useEffect(() => {
        setData(languages);
    }, [languages]);

    const handlePageChange = (event, pageNumber) => {
        setPage(pageNumber);
    };

    const handleRowsPerPage = (value) => {
        setSize(parseInt(value));
        setPage(1);
    };

    const handleToggleActive = (row) => {
        if (!hasPermission) return permissionError();
        dispatch(toggleLanguage({ languageCode: row.languageCode, toggleType: 1 }));
    };

    const handleToggleDefault = (row) => {
        if (!hasPermission) return permissionError();
        dispatch(toggleLanguage({ languageCode: row.languageCode, toggleType: 2 }));
    };

    const handleDelete = (row) => {
        warning().then((res) => {
            if (!res) return;
            if (!hasPermission) return permissionError();
            dispatch(deleteLanguage(row.languageCode));
        });
    };

    const handleDownloadCSV = () => {
        warning().then((res) => {
            if (!res) return;
            if (!hasPermission) return permissionError();
            dispatch(downloadTranslationsCSV()).then((res) => {
                if (res && res.data) {
                    const blob = new Blob([res.data], { type: "text/csv" });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "translations.csv");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }
            }).catch((err) => {
                console.log("Download Error: ", err);
            });
        });
    };

    const handleFilterData = (filteredData) => {
        if (typeof filteredData === "string") {
            setSearch(filteredData);
            setPage(1);
        } else {
            setData(filteredData);
        }
    };

    const columns = [
        {
            Header: "No",
            width: "20px",
            Cell: ({ index }) => (
                <span className="text-white">{(page - 1) * size + index + 1}</span>
            ),
        },
        {
            Header: "Image",
            body: "languageIcon",
            Cell: ({ row }) => {
                const imageUrl = row?.languageIcon
                    ? row.languageIcon.startsWith("http")
                        ? row.languageIcon
                        : `${baseURL}${row.languageIcon}`
                    : "/images/defaultImageGlobal.png";

                return (
                    <img
                        src={imageUrl}
                        alt="Icon"
                        style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "8px", cursor: "pointer" }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!row?.languageIcon) return;
                            setSelectedImage(imageUrl);
                            setShowImageModal(true);
                        }}
                        onError={(e) => { e.target.src = "/defaultImage.png" }}
                    />
                );
            },
        },
        {
            Header: "Title",
            body: "languageTitle",
            Cell: ({ row }) => <span className="text-capitalize text-white">{row?.languageTitle}</span>,
        },
        {
            Header: "Code",
            body: "languageCode",
            Cell: ({ row }) => <span className="text-uppercase text-white">{row?.languageCode}</span>,
        },
        {
            Header: "Localized Title",
            body: "localLanguageTitle",
            Cell: ({ row }) => <span className="text-capitalize text-white">{row?.localLanguageTitle}</span>,
        },
        ...(canEdit ? [
            {
                Header: "Active",
                body: "isActive",
                Cell: ({ row }) => (
                    <ToggleSwitch
                        value={row?.isActive}
                        onClick={() => handleToggleActive(row)}
                    />
                ),
            }
        ] : []),
        ...(canEdit ? [
            {
                Header: "Default",
                body: "isDefault",
                Cell: ({ row }) => (
                    <ToggleSwitch
                        value={row?.isDefault}
                        onClick={() => handleToggleDefault(row)}
                    />
                ),
            }
        ] : []),
        {
            Header: "Errors",
            body: "errorCount",
            Cell: ({ row }) => <span style={{ color: row?.errorCount > 0 ? "#ff4c51" : "#4caf50", fontWeight: "bold" }}>{row?.errorCount || 0}</span>,
        },
        ...(canEdit ? [
            {
                Header: "Edit",
                body: "action",
                Cell: ({ row }) => (
                    <Iconb
                        newClass={`themeFont boxCenter infobtn userBtn fs-5`}
                        btnIcon={<CreateOutlinedIcon sx={{ color: '#737272' }} />}
                        style={{
                            borderRadius: "50px",
                            margin: "auto",
                            height: "45px",
                            width: "45px",
                            color: "#160d98",


                        }}
                        isImage={true}
                        onClick={() => dispatch({ type: OPEN_DIALOGUE, payload: { type: "language", data: row } })}

                    />

                ),
            }
        ] : []),
        ...(canDelete ? [
            {
                Header: "Delete",
                body: "action",
                Cell: ({ row }) => (
                    <Iconb
                        newClass={`themeFont boxCenter killbtn userBtn fs-5`}
                        btnIcon={<DeleteIcon sx={{ color: '#FF4C51' }} />}
                        style={{
                            borderRadius: "50px",
                            margin: "auto",
                            height: "45px",
                            width: "45px",
                            color: "#160d98",

                            padding: "0px"

                        }}
                        isImage={true}
                        isDeleted={true}
                        onClick={() => handleDelete(row)}

                    />
                ),
            }
        ] : []),
        {
            Header: "Info",
            body: "action",
            Cell: ({ row }) => (
                <Iconb
                    newClass={`themeFont boxCenter infobtn userBtn fs-5`}
                    btnIcon={<InfoOutlined sx={{ color: '#737272' }} />}
                    style={{
                        borderRadius: "50px",
                        margin: "auto",
                        height: "45px",
                        width: "45px",
                        color: "#160d98",

                        padding: "0px"
                    }}
                    isImage={true}
                    isDeleted={true}
                    onClick={() => dispatch({ type: OPEN_DIALOGUE, payload: { type: "translationInfo", data: row } })}

                />
            ),
        }
    ];

    return (
        <>
            <div className="mainUserTable">
                <div className="userTable">
                    <div className="col-12 headname">App Language</div>
                    <div className="userMain">
                        <div className="tableMain mt-2">
                            <div className="userHeader primeHeader">
                                <div className="row align-items-center">
                                    <div className="col-12 col-md-7 d-flex flex-wrap gap-2 align-items-center  justify-content-md-start">
                                        {canCreate && (
                                            <Button
                                                newClass={`whiteFont`}
                                                btnColor={`btnBlackPrime`}
                                                btnIcon={`fa-solid fa-plus`}
                                                btnName={`Add`}
                                                onClick={() => dispatch({ type: OPEN_DIALOGUE, payload: { type: "language" } })}
                                                style={{ borderRadius: "5px", padding: "8px 25px", background: "#b93160" }}
                                            />
                                        )}
                                        {canEdit && (
                                            <Button
                                                newClass={`whiteFont`}
                                                btnColor={`btnBlackPrime`}
                                                btnIcon={"fa-solid fa-arrow-up"}
                                                btnName={"Upload"}
                                                onClick={() => dispatch({ type: OPEN_DIALOGUE, payload: { type: "uploadCSV" } })}
                                                style={{ borderRadius: "5px", background: "#b93160", padding: "8px 25px" }}
                                            />
                                        )}
                                        <Button
                                            newClass={`whiteFont`}
                                            btnColor={`btnBlackPrime`}
                                            btnIcon={"fa-solid fa-arrow-down"}
                                            btnName={"Download"}
                                            onClick={handleDownloadCSV}
                                            style={{ borderRadius: "5px", background: "#b93160", padding: "8px 25px" }}
                                        />
                                    </div>
                                    <div className="col-12 col-md-5 d-flex justify-content-center justify-content-md-end mt-3 mt-sm-0">
                                        <Searching
                                            type="server"
                                            data={languages}
                                            setData={setData}
                                            onFilterData={handleFilterData}
                                            serverSearching={handleFilterData}
                                            button={true}
                                            setSearchValue={setSearch}
                                            searchValue={search}
                                            placeholder="Search by language title or code"
                                        />
                                    </div>
                                </div>
                            </div>
                            <Table
                                data={data}
                                mapData={columns}
                                serverPerPage={size}
                                serverPage={page}
                                type={"server"}
                                loading={loading}
                                shimmer={true}
                            />
                            <Pagination
                                type={"server"}
                                serverPage={page}
                                serverPerPage={size}
                                totalData={totalLanguages}
                                setCurrentPage={setPage}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPage}
                            />
                        </div>
                    </div>
                    <div className="userFooter primeFooter"></div>
                </div>
            </div>
            <ImagePreviewModal
                isOpen={showImageModal}
                onClose={() => {
                    setShowImageModal(false);
                    setSelectedImage("");
                }}
                image={selectedImage}
            />
        </>
    );
};

export default LanguageTable;
